import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { percentageFormatter } from 'src/util/grid/cellFormatter';

// Base column definitions for MSS grid (used by all services)
const baseMssGridColDefs: ColDef[] = [
    {
        field: 'month',
        headerName: 'Month',
        filter: 'agSetColumnFilter',
        flex: 1,
        minWidth: 100,
        sortable: true
    },
    {
        field: 'territory',
        headerName: 'Territory',
        flex: 1,
        minWidth: 100,
        sortable: true
    },
    {
        field: 'vendor',
        headerName: 'Vendor',
        flex: 2,
        minWidth: 150,
        sortable: true
    },
    {
        field: 'vendorPlays',
        headerName: 'Vendor Plays',
        filter: 'agNumberColumnFilter',
        flex: 1,
        minWidth: 150,
        sortable: true,
        valueFormatter: (params) => {
            if (params.value == null) return '0';
            return params.value.toLocaleString();
        }
    },
    {
        field: 'totalPlays',
        headerName: 'Total Plays',
        filter: 'agNumberColumnFilter',
        flex: 1,
        minWidth: 150,
        sortable: true,
        valueFormatter: (params) => {
            if (params.value == null) return '0';
            return params.value.toLocaleString();
        }
    },
    {
        field: 'mss',
        headerName: 'MSS',
        filter: 'agNumberColumnFilter',
        flex: 1,
        minWidth: 150,
        sortable: true,
        valueFormatter: percentageFormatter
    }
];

// Music-only column definitions (only for AMU serviceId=1)
const musicOnlyColDefs: ColDef[] = [
    {
        field: 'musicOnlyVendorPlays',
        headerName: 'MusicOnly Vendor Plays',
        filter: 'agNumberColumnFilter',
        flex: 1,
        minWidth: 180,
        sortable: true,
        valueFormatter: (params) => {
            if (params.value == null) return '';
            return params.value.toLocaleString();
        }
    },
    {
        field: 'musicOnlyTotalPlays',
        headerName: 'MusicOnly Total Plays',
        filter: 'agNumberColumnFilter',
        flex: 1,
        minWidth: 180,
        sortable: true,
        valueFormatter: (params) => {
            if (params.value == null) return '';
            return params.value.toLocaleString();
        }
    },
    {
        field: 'musicOnlyMss',
        headerName: 'MusicOnly MSS',
        filter: 'agNumberColumnFilter',
        flex: 1,
        minWidth: 150,
        sortable: true,
        valueFormatter: (params) => {
            if (params.value == null) return '';
            return percentageFormatter(params);
        }
    }
];

/**
 * Generate MSS grid column definitions based on service ID
 * @param serviceId - The service ID (1 for AMU, others for Prime/AMF)
 * @returns Column definitions array
 */
export const getMssGridColDefs = (serviceId?: number): ColDef[] => {
    const isAmuService = serviceId === 1;

    if (!isAmuService) {
        return baseMssGridColDefs;
    }

    // For AMU service, insert MSS Type column between Vendor and Vendor Plays
    const columns: ColDef[] = [];

    baseMssGridColDefs.forEach((col, index) => {
        columns.push(col);

        // Insert MSS Type column after Vendor column (index 2: Month, Territory, Vendor)
        if (index === 2) {
            columns.push({
                field: 'isArtistCentric',
                headerName: 'MSS Type',
                filter: 'agSetColumnFilter',
                flex: 1,
                minWidth: 120,
                sortable: true,
                valueGetter: (params: ValueGetterParams) => {
                    if (params.data.isArtistCentric == true) return 'Artist Centric';
                    if (params.data.isArtistCentric == false) return 'Standard';
                    return '';
                }
            });
        }
    });

    // Add music-only columns at the end
    return [...columns, ...musicOnlyColDefs];
};

// Default export for backward compatibility (AMU)
export const mssGridColDefs: ColDef[] = getMssGridColDefs(1);
